import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import { scrollToTop } from "@/utils";
import {
  Award,
  BookOpen,
  Calendar,
  CheckCircle2,
  Clock,
  Heart,
  History,
  Layers,
  MapPin,
  Music,
  Shield,
  Sparkles,
  Users,
} from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const PageAbout = () => {
  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col justify-between">
      <Helmet>
        <title>À Propos | Église de Dieu Garizim - Histoire, Mission & Foi</title>
        <meta
          name="description"
          content="Découvrez l'histoire de l'Église de Dieu Garizim depuis 1977, notre équipe pastorale, notre confession de foi évangélique et nos ministères à Montréal et sur la Rive-Sud."
        />
        <link rel="canonical" href="https://egarizim.com/a-propos" />
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-800 text-white py-16 sm:py-24 px-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="container mx-auto max-w-5xl text-center relative z-10 space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-amber-300 text-xs sm:text-sm font-bold uppercase tracking-wider backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>Plus de 48 Ans de Foi & de Service</span>
            </div>

            <h1 className="text-3xl xs:text-4xl sm:text-6xl font-black tracking-tight leading-tight">
              Une Communauté Vivante au Cœur du Grand Montréal
            </h1>

            <p className="text-slate-300 text-base sm:text-xl max-w-3xl mx-auto leading-relaxed">
              Fondée en 1977, l'Église de Dieu Garizim proclame l'Évangile de
              Jésus-Christ, édifie les croyants par la saine doctrine biblique et
              sert avec amour la communauté à Montréal et sur la Rive-Sud.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-300 bg-white/5 border border-white/10 px-4 py-2 rounded-full">
                <Calendar className="w-4 h-4 text-amber-400" />
                <span>Fondée en 1977</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-300 bg-white/5 border border-white/10 px-4 py-2 rounded-full">
                <MapPin className="w-4 h-4 text-amber-400" />
                <span>Montréal & Rive-Sud</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-300 bg-white/5 border border-white/10 px-4 py-2 rounded-full">
                <Users className="w-4 h-4 text-amber-400" />
                <span>Plusieurs Générations Unies</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Grid */}
        <section className="container mx-auto max-w-5xl px-4 sm:px-6 -mt-8 relative z-20 space-y-12">
          {/* 1. Notre Mission & Charte Éditoriale */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200/80 p-6 sm:p-12 space-y-8">
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
                Vocation Spirituelle
              </span>
              <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                Notre Mission & Ligne Éditoriale
              </h2>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                La plateforme web <strong>egarizim.com</strong> s'inscrit dans le
                mandat spirituel de la Grande Commission donnée par le Seigneur
                Jésus (Matthieu 28:19-20). Notre mission repose sur trois piliers
                fondamentaux :
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="w-12 h-12 rounded-xl bg-church-blue-100 flex items-center justify-center text-church-blue-700 font-bold">
                  <BookOpen className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  Enseignement Biblique
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Publier des études doctrinales approfondies, des réflexions
                  pastorales authentiques et des guides de vie chrétienne
                  solidement enracinés dans les Saintes Écritures.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                  <Music className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  Héritage des Cantiques
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Préserver et mettre à disposition gratuitement l'intégralité du
                  répertoire numérisé des <em>Chants d'Espérance</em> pour nourrir
                  la louange des croyants de toutes générations.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  Communion & Famille
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Offrir un espace d'accueil chaleureux, d'intercession et
                  d'accompagnement pour les familles, les nouveaux arrivants et
                  les jeunes chrétiens au Québec.
                </p>
              </div>
            </div>
          </div>

          {/* 2. Notre Histoire (1977 à aujourd'hui) */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200/80 p-6 sm:p-12 space-y-8">
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-church-blue-700 bg-church-blue-50 px-3 py-1 rounded-full border border-church-blue-200">
                Patrimoine & Racines
              </span>
              <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                Une Histoire Écrite par la Grâce Divine
              </h2>
              <div className="space-y-4 text-slate-700 text-sm sm:text-base leading-relaxed">
                <p>
                  L'histoire de l'Église de Dieu de Garizim débute en 1977 à
                  Montréal, portée par des familles chrétiennes pionnières
                  originaires d'Haïti et des Antilles, désireuses d'élever un
                  autel de prière dans la belle province du Québec. Dès son
                  origine, la communauté a choisi le nom biblique de{" "}
                  <strong>Garizim</strong> en mémoire de la montagne de la
                  bénédiction proclamée en Deutéronome 11 et 27.
                </p>
                <p>
                  Au fil des décennies, l'assemblée a franchi avec foi les étapes
                  de sa consolidation : de réunions de prière en appartements aux
                  locaux temporaires, jusqu'à la construction sacrificielle de
                  son grand sanctuaire actuel au{" "}
                  <strong>11800 4e Avenue à Montréal</strong>. Cet édifice moderne
                  est le fruit de l'engagement bénévole et des dons généreux de
                  plusieurs générations de croyants.
                </p>
                <p>
                  Pour répondre aux besoins des fidèles résidant au sud de la
                  métropole, un second campus dynamique a été implanté sur la
                  Rive-Sud au <strong>2600 Rue Grant à Longueuil</strong>,
                  renforçant ainsi notre présence missionnaire et pastorale de
                  proximité.
                </p>
              </div>
            </div>

            {/* Timeline Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 pt-4 border-t border-slate-100">
              <div className="text-center p-4 rounded-xl bg-slate-50">
                <div className="text-2xl sm:text-3xl font-black text-church-blue-800">
                  1977
                </div>
                <div className="text-xs text-slate-600 font-medium mt-1">
                  Fondation de l'Église
                </div>
              </div>
              <div className="text-center p-4 rounded-xl bg-slate-50">
                <div className="text-2xl sm:text-3xl font-black text-church-blue-800">
                  11800
                </div>
                <div className="text-xs text-slate-600 font-medium mt-1">
                  Sanctuaire 4e Avenue
                </div>
              </div>
              <div className="text-center p-4 rounded-xl bg-slate-50">
                <div className="text-2xl sm:text-3xl font-black text-church-blue-800">
                  2 Campus
                </div>
                <div className="text-xs text-slate-600 font-medium mt-1">
                  Montréal & Rive-Sud
                </div>
              </div>
              <div className="text-center p-4 rounded-xl bg-slate-50">
                <div className="text-2xl sm:text-3xl font-black text-amber-600">
                  48+ Ans
                </div>
                <div className="text-xs text-slate-600 font-medium mt-1">
                  De fidélité ininterrompue
                </div>
              </div>
            </div>
          </div>

          {/* 3. Confession de Foi */}
          <div className="bg-slate-900 text-white rounded-3xl shadow-xl p-6 sm:p-12 space-y-8">
            <div className="space-y-3">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20">
                Fondements Doctrিনaux
              </span>
              <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight">
                Notre Confession de Foi
              </h2>
              <p className="text-slate-300 text-sm sm:text-base">
                L'Église de Dieu Garizim professe la foi chrétienne évangélique
                dans la lignée de l'héritage réformé et apostolique :
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/5 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-white text-sm sm:text-base">
                    L'Autorité des Écritures
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-300 mt-1">
                    Nous croyons que la Bible est la Parole inspirée, infaillible
                    et souveraine de Dieu, seule autorité suprême en matière de
                    foi et de pratique.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/5 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-white text-sm sm:text-base">
                    Le Dieu Trine
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-300 mt-1">
                    Nous croyons en un seul Dieu éternel, existant en trois
                    personnes coéternelles et consubstantielles : le Père, le Fils
                    et le Saint-Esprit.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/5 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-white text-sm sm:text-base">
                    Le Salut par la Grâce Seule
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-300 mt-1">
                    Le pardon des péchés et la vie éternelle sont offerts
                    gratuitement par pure grâce, par le moyen de la foi dans le
                    sacrifice parfait de Jésus-Christ à la croix.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 rounded-2xl bg-white/5 border border-white/10">
                <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-white text-sm sm:text-base">
                    L'Action du Saint-Esprit
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-300 mt-1">
                    Nous croyons en l'œuvre régénératrice du Saint-Esprit qui
                    habite chaque croyant, lui confère Ses dons et le rend capable
                    de vivre une vie sainte et victorieuse.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Direction Pastorale */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200/80 p-6 sm:p-12 space-y-8">
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-church-blue-700 bg-church-blue-50 px-3 py-1 rounded-full border border-church-blue-200">
                Direction Spirituelle
              </span>
              <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                L'Équipe Pastorale & Les Dirigeants
              </h2>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
                Notre communauté est conduite par un corps pastoral dévoué,
                assisté par des diacres, des éducateurs chrétiens et des leaders
                de ministères œuvrant bénévolement au service du troupeau de
                Dieu :
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* 1. Pasteur Serge Joseph */}
              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center gap-3">
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/pasteur-femme.png?updatedAt=1760916546786"
                    alt="Pasteur Serge Joseph"
                    className="w-14 h-14 rounded-full object-cover border-2 border-amber-300 shadow-sm"
                  />
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">
                      Pasteur Serge Joseph
                    </h3>
                    <p className="text-xs text-amber-700 font-semibold uppercase tracking-wider">
                      Pasteur Fondateur
                    </p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Fondateur de Garizim avec une vision apostolique claire, il a
                  consacré plusieurs décennies au ministère pastoral et à la
                  proclamation de la saine doctrine avec un amour paternel pour
                  chaque famille.
                </p>
              </div>

              {/* 2. Pasteur Othniel Joseph */}
              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center gap-3">
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/othniel.jpg?updatedAt=1760896561970"
                    alt="Pasteur Othniel Joseph"
                    className="w-14 h-14 rounded-full object-cover border-2 border-slate-300 shadow-sm"
                  />
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">
                      Pasteur Othniel Joseph
                    </h3>
                    <p className="text-xs text-church-blue-700 font-semibold uppercase tracking-wider">
                      Pasteur Titulaire
                    </p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Pasteur titulaire conduisant l'assemblée avec ferveur et
                  sagesse, engagé à accompagner chaque croyant dans une marche
                  de foi authentique, de partage et de croissance spirituelle.
                </p>
              </div>

              {/* 3. Pasteur Serge Joseph Jr. */}
              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center gap-3">
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/serge-junior.jpg?updatedAt=1760896562157"
                    alt="Pasteur Serge Joseph Jr."
                    className="w-14 h-14 rounded-full object-cover border-2 border-sky-300 shadow-sm"
                  />
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">
                      Pasteur Serge Joseph Jr.
                    </h3>
                    <p className="text-xs text-sky-700 font-semibold uppercase tracking-wider">
                      Pasteur Associé
                    </p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Engagé à marcher aux côtés de l'assemblée sur le chemin de la
                  foi, de l'amour fraternel et de l'approfondissement des
                  enseignements bibliques pour l'édification de la communauté.
                </p>
              </div>

              {/* 4. Pasteur Jimmy Jeanty */}
              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-center gap-3">
                  <img
                    src="https://ik.imagekit.io/4apzi8lz0/egarizim/jimmy.jpg?updatedAt=1760896561686"
                    alt="Pasteur Jimmy Jeanty"
                    className="w-14 h-14 rounded-full object-cover border-2 border-sky-300 shadow-sm"
                  />
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">
                      Pasteur Jimmy Jeanty
                    </h3>
                    <p className="text-xs text-sky-700 font-semibold uppercase tracking-wider">
                      Pasteur Associé
                    </p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  Au service de cette communauté depuis de nombreuses années, il
                  veille avec affection pastorale au soutien spirituel de chacun
                  et à la joie de servir le Seigneur dans l'unité fraternelle.
                </p>
              </div>
            </div>
          </div>

          {/* 5. Ministères & Impact Communautaire */}
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200/80 p-6 sm:p-12 space-y-8">
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                Action & Rayonnement
              </span>
              <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                Nos Départements & Ministères
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-church-blue-600" />
                  Jeunesse Garizim
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Rencontres hebdomadaires, conférences d'orientation, retraites
                  spirituelles et activités fraternelles pour équiper les jeunes
                  chrétiens face aux défis contemporains.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-church-blue-600" />
                  École du Dimanche
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Classes adaptées par tranche d'âge pour enseigner les
                  fondements bibliques aux enfants dès leur plus jeune âge avec
                  des méthodes pédagogiques bienveillantes.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Music className="w-4 h-4 text-church-blue-600" />
                  Chorales & Musique
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Groupes musicaux et chorales interprétant le répertoire des
                  Chants d'Espérance et de louange contemporaine pour élever
                  l'assemblée dans la présence du Très-Haut.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Heart className="w-4 h-4 text-church-blue-600" />
                  Entraide & Diaconie
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Soutien moral et matériel aux personnes vulnérables, visites
                  pastorales aux malades et accompagnement des nouveaux arrivants
                  dans leur intégration au Québec.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Layers className="w-4 h-4 text-church-blue-600" />
                  Dames & Hommes d'Action
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Rassemblements dédiés aux épouses, mères et pères de famille
                  pour fortifier les couples et encourager la sanctification du
                  foyer chrétien.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-2">
                <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-church-blue-600" />
                  Cellules d'Intercession
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Réunions de prière et de jeûne en semaine pour porter les
                  besoins spirituels, le salut des âmes et la paix dans le monde.
                </p>
              </div>
            </div>
          </div>

          {/* Quick links banner */}
          <div className="bg-slate-950 text-white rounded-3xl p-6 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center sm:text-left">
              <h3 className="text-xl sm:text-2xl font-bold">
                Vous souhaitez nous rendre visite ?
              </h3>
              <p className="text-sm text-slate-400">
                Nos cultes d'adoration sont ouverts à tous chaque dimanche à
                Montréal (10h) et sur la Rive-Sud (14h).
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/services"
                className="bg-church-gold-500 hover:bg-church-gold-600 text-black font-bold px-5 py-2.5 rounded-full text-sm transition-colors shadow"
              >
                Horaires des Services
              </Link>
              <Link
                to="/contact"
                className="bg-white/10 hover:bg-white/20 text-white font-bold px-5 py-2.5 rounded-full text-sm transition-colors border border-white/15"
              >
                Nous Écrire
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PageAbout;
